import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { readStorage, writeStorage } from 'src/app/shared/utils/safe-storage';
import { AppUpdateGate } from '../offline/app-update-gate';

const CHUNK_ERROR_MESSAGE_SIGNATURES: ReadonlyArray<string> = [
  'Loading chunk',
  'Failed to fetch dynamically imported module',
  'error retrieving children',
  'errore durante il recupero dei children',
];

const RELOAD_GUARD_KEY = 'chunkReloadAt';
const RELOAD_GUARD_WINDOW_MS = 10_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = error.message ?? '';
  return CHUNK_ERROR_MESSAGE_SIGNATURES.some(sig => msg.includes(sig));
}

@Injectable({ providedIn: 'root' })
export class ChunkErrorRecoveryService {
  private readonly needsReloadSubject = new BehaviorSubject<boolean>(false);
  // A newer build is ready (OfflineShellService, after a deploy): how to move the tab onto it. Offered
  // at once; same banner as a chunk error, but the reload is the user's click, never automatic, and
  // only the click honours the update gate. `apply` answers false when it held the click back (a
  // download a reload would cut short).
  private readonly newBuildSubject = new BehaviorSubject<(() => boolean) | null>(null);
  /**
   * "Più tardi" on a new build's banner (review round 8): unlike a chunk error, which appeared rarely
   * and on a page that cannot load its code, the new build is offered at every deploy, and the fixed
   * banner covered the pages' sticky headers until a reload the user may not want yet (a cart, a
   * form). Hidden for that build; the next one, a chunk error or the engine's folder gone show it
   * again, not dismissible.
   */
  private readonly dismissedSubject = new BehaviorSubject<boolean>(false);
  readonly needsReload$: Observable<boolean> = combineLatest([
    this.needsReloadSubject,
    this.newBuildSubject,
    this.dismissedSubject,
  ]).pipe(
    map(([chunk, build, dismissed]) => chunk || (build !== null && !dismissed)),
    // one value per change: offering a build moves two subjects
    distinctUntilChanged(),
  );
  /** Whether the banner may be put off: a new build is the only reason it shows. */
  readonly canDismiss$: Observable<boolean> = combineLatest([
    this.needsReloadSubject,
    this.newBuildSubject,
  ]).pipe(
    map(([chunk, build]) => !chunk && build !== null),
    distinctUntilChanged(),
  );
  /**
   * A real chunk error was seen (`tryReloadOrNotify`): the page cannot load its code, so the click
   * reloads whatever holds the update gate. `offerReload` alone does not set it.
   */
  private chunkFailed = false;

  constructor(private readonly gate: AppUpdateGate) {}

  /**
   * Offer the switch to a newer build: the update banner shows (again, if an earlier build's was put
   * off) and its button runs `apply`. Lives here so the banner, which is in the main bundle, does
   * not import the lazily loaded OfflineShellService. An offer is never withdrawn: the page is on
   * the old build until it reloads.
   */
  offerNewBuild(apply: () => boolean): void {
    this.dismissedSubject.next(false);
    this.newBuildSubject.next(apply);
  }

  /** "Più tardi": the new build's banner hidden; a chunk error's (or the engine's) is not. */
  dismissNewBuild(): void {
    if (!this.needsReloadSubject.value && this.newBuildSubject.value) {
      this.dismissedSubject.next(true);
    }
  }

  /**
   * Show the update banner for a code load that failed because the deployed build moved on (the
   * scan engine's version folder gone from the server, 25/10/2026), WITHOUT the automatic reload a
   * chunk error gets: the failure surfaces while the user is on a page (Scan, a list being built),
   * so the reload is their click. `reloadNow` then reloads, recording the loop guard.
   */
  offerReload(): void {
    this.needsReloadSubject.next(true);
  }

  tryReloadOrNotify(): void {
    this.chunkFailed = true;
    if (this.recentlyReloaded()) {
      this.needsReloadSubject.next(true);
      return;
    }
    // The loop guard IS the stored timestamp: it is the only thing that survives the reload we are
    // about to trigger. If storage refuses the write we cannot tell "first attempt" from "tenth" on
    // the next page, so an auto-reload here could loop forever on a persistent chunk error. Fall
    // back to asking the user instead.
    if (!this.recordReload()) {
      this.needsReloadSubject.next(true);
      return;
    }
    this.reloadPage();
  }

  /** Explicit user action (the update banner's button): reloads, loop guard or no guard. A pending
   *  new build without a chunk error reloads through its own `apply` (its gate and loop guard).
   *  Without a real chunk error (`offerReload`: the engine's folder gone, alone or with a new
   *  build) the update gate is honoured too (review round 7): a reload would cut Scan's offline
   *  downloads short. Answers false when the click was held back: nothing happened, and the
   *  banner says why. */
  reloadNow(): boolean {
    const apply = this.newBuildSubject.value;
    if (apply && !this.needsReloadSubject.value) {
      return apply();
    }
    if (!this.chunkFailed && this.gate.held) {
      return false;
    }
    this.recordReload();
    this.reloadPage();
    return true;
  }

  private recentlyReloaded(): boolean {
    const raw = readStorage('session', RELOAD_GUARD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < RELOAD_GUARD_WINDOW_MS;
  }

  private recordReload(): boolean {
    return writeStorage('session', RELOAD_GUARD_KEY, String(Date.now()));
  }

  private reloadPage(): void {
    location.reload();
  }
}
